import React, { useState, useEffect } from "react";
import { Tooltip } from "@mui/material";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Toaster, toast } from "react-hot-toast";
import { imageBaseURL, Base_URL } from "actions/Base_URL";
import { useLocation } from "react-router-dom";
import SubmittingModal from "components/Modal";

export default function CreateInventory() {
  const navigate = useNavigate();
  const [labData, setLabData] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    quantity: 1,
    lab_id: "",
    description: "",
    inventory_images: [],
    existing_images: [],
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false); // Added loading state for submit button
  const location = useLocation();
  const { type, id } = location.state;
  const user_id = localStorage.getItem("user_id");
  //console.log(type,id);

  // Consolidated handleChange function for all form fields
  // This function will now handle all inputs: text, number, select, and file
  const handleChange = (e) => {
    const { name, value, type } = e.target;
    if (name === "inventory_images") {
      const files = e.target.files;
      setFormData({ ...formData, [e.target.name]: files });
    } else {
      setFormData((prevFormData) => {
        let newValue;
        if (type === "number") {
          newValue = value === "" ? "" : Number(value); // Convert to number, but keep empty string if input is empty
        } else {
          newValue = value;
        }

        return {
          ...prevFormData,
          [name]: newValue,
        };
      });
    }

    // Clear error for the field being changed
    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
  };

  const handleImageChange = (indexToRemove) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      existing_images: prevFormData.existing_images.filter(
        (_, index) => index !== indexToRemove
      ),
    }));
  };

  async function getLabDropdown() {
    try {
      const token = localStorage.getItem("token");
      let response = await axios.get(`${Base_URL}/labs/getAllLabs`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      //console.log("rl",response.data.data);
      setLabData(response.data.data);
    } catch (error) {
      setLabData([]);
      toast.error("Something went wrong while fetching labs.");
      console.error("Error fetching labs:", error); // Log the actual error
    }
  }

  function validateForm() {
    let newErrors = {};
    let isValid = true;

    if (!formData.name.trim()) {
      newErrors.name = "Inventory name is required";
      isValid = false;
    }

    // Quantity validation: must be a positive number
    if (
      formData.quantity === "" ||
      isNaN(formData.quantity) ||
      Number(formData.quantity) <= 0
    ) {
      newErrors.quantity = "Enter a valid quantity (greater than 0)";
      isValid = false;
    }

    // Lab selection validation (using lab_id, which is stored in formData)
    if (!formData.lab_id) {
      // Check if lab_id is selected (empty string means nothing selected)
      newErrors.lab_id = "Please select a lab"; // Error message specific to lab_id
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  }

  async function onSubmitFormData(e) {
    e.preventDefault(); // Prevent default form submission behavior

    // **Crucial: Call validateForm() and return if it's not valid**
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true); // Start loading
      const token = localStorage.getItem("token");
      const dataToSend = new FormData(); // Use FormData for sending files and other data

      dataToSend.append("name", formData.name);
      dataToSend.append("quantity", formData.quantity);
      dataToSend.append("lab_id", formData.lab_id); // Now sending lab_id
      dataToSend.append("description", formData.description);
      // if( formData.inventory_images.length !== 0){
      //   Array.from(formData.inventory_images).forEach((image) => {
      //     //console.log(image);
      //     dataToSend.append("inventory_images", image); // append all selected images
      //     });

      // }
      if (formData.inventory_images && formData.inventory_images.length > 0) {
        Array.from(formData.inventory_images).forEach((image) => {
          if (image instanceof File && image.type.startsWith("image/")) {
            dataToSend.append("inventory_images", image);
          }
        });
      }
      // if( formData.existing_images.length !== 0){
      //   Array.from(formData.existing_images).forEach((image) => {
      //       dataToSend.append("existing_images", image); // append all selected images
      //     });
      // }

      if (formData.existing_images && formData.existing_images.length > 0) {
        dataToSend.append(
          "existing_images",
          JSON.stringify(formData.existing_images)
        );
      }

      if (type === "create") {
        const res = await axios.post(
          `${Base_URL}/api/inventory/createInventory`,
          dataToSend,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              // 'Content-Type' is automatically set to 'multipart/form-data' by axios when using FormData
            },
          }
        );

        if (res.status === 201 || res.status === 200) {
          toast.success("Facility Reserved Successfully!");
        } else {
          toast.error("Something went wrong.", res.err);
        }
      } else {
        const res = await axios.put(
          `${Base_URL}/api/inventory/updateInventory/${id}`,
          dataToSend,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (res.status === 201 || res.status === 200) {
          toast.success("Facility Reservation Updated Successfully!");
        } else {
          toast.error("Something went wrong.", res.err);
        }
      }

      toast.success("Inventory created successfully!");
      setFormData({
        name: "",
        quantity: 1,
        lab_id: "",
        description: "",
        inventory_images: [],
        existing_images: [],
      });
      setErrors({});

      setTimeout(() => {
        navigate("/admin/inventory");
      }, 1000);
    } catch (error) {
      console.error("Error creating inventory:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Failed to create inventory. Please try again.";
      toast.error(errorMessage);
    } finally {
      setLoading(false); // End loading
    }
  }

  const fetchInventoryData = async (id) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      // Make sure the API endpoint is correct for fetching single inventory item
      const response = await axios.get(
        `${Base_URL}/api/inventory/getInventoryById/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      // The response.data directly contains the inventory item
      const inventoryItem = response.data;
      //console.log("it",inventoryItem);
      setFormData({
        name: inventoryItem.name,
        quantity: inventoryItem.quantity,
        lab_id: inventoryItem.lab_id, // Use lab_id from the fetched inventory
        description: inventoryItem.description,
        existing_images: inventoryItem.inventory_images,
        inventory_images: [],
      });
    } catch (error) {
      console.error("Error fetching inventory data:", error);
      toast.error("Failed to fetch inventory data");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    getLabDropdown();
    if (type !== "create") {
      fetchInventoryData(id);
    }
  }, []);
  return (
    <>
      <Toaster position="bottom-left" reverseOrder={false} />
      <SubmittingModal loading={loading} />
      <div className="flex justify-center items-center">
        <div className="relative flex flex-col min-w-0 break-words w-full mb-6 mt-6 shadow-lg rounded-lg bg-dark-secondary border-0">
          <div className="rounded-t bg-dark-tertiary mb-0 px-6 py-6">
            <div className="text-center flex justify-between">
              <h6 className="text-white text-xl font-bold">
                {type === "create"
                  ? "Create "
                  : type === "edit"
                    ? "Update "
                    : "View "}{" "}
                Inventory
              </h6>
            </div>
          </div>
          <div className="flex-auto px-4 lg:px-10 py-10 pt-0">
            {/* The onSubmit handler should be on the form element */}
            <form onSubmit={onSubmitFormData}>
              <h6 className="text-gray-400 text-1xl mt-3 mb-6 font-bold ">
                Inventory Information
              </h6>
              <div className="flex flex-wrap">
                <div className="w-full lg:w-6/12 px-4">
                  <div className="relative w-full mb-3">
                    <label className="block text-gray-300 text-xs font-bold mb-2">
                      Inventory Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      placeholder="Inventory Name"
                      className="border-0 px-3 py-3 placeholder-gray-500 text-white bg-dark-primary rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      disabled={type === "view"}
                    />
                    {errors.name && (
                      <p className="text-red-500 text-xs">{errors.name}</p>
                    )}
                  </div>
                </div>
                <div className="w-full lg:w-6/12 px-4">
                  <div className="relative w-full mb-3">
                    <label className="block text-gray-300 text-xs font-bold mb-2">
                      Quantity <span className="text-red-500">*</span>
                    </label>
                    <input
                      placeholder="Quantity"
                      className="border-0 px-3 py-3 placeholder-gray-500 text-white bg-dark-primary rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                      type="number"
                      name="quantity"
                      min="1"
                      value={formData.quantity}
                      onChange={handleChange}
                      disabled={type === "view"}
                    />
                    {errors.quantity && (
                      <p className="text-red-500 text-xs">{errors.quantity}</p>
                    )}
                  </div>
                </div>
                <div className="w-full lg:w-6/12 px-4">
                  <div className="relative w-full mb-3">
                    <label className="block text-gray-300 text-xs font-bold mb-2">
                      Lab <span className="text-red-500">*</span>
                    </label>
                    <select
                      className="border-0 px-3 py-3 placeholder-gray-500 text-white bg-dark-primary rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                      name="lab_id"
                      value={formData.lab_id}
                      onChange={handleChange}
                      disabled={type === "view"}
                    >
                      <option value="">Select Lab</option>
                      {labData.map((lab) => (
                        // Use lab.lab_id for the option value as it matches formData.lab_id
                        <option key={lab.lab_id} value={lab.lab_id}>
                          {lab.lab_name}
                        </option>
                      ))}
                    </select>
                    {/* Error for lab_id, not lab_name */}
                    {errors.lab_id && (
                      <p className="text-red-500 text-xs">{errors.lab_id}</p>
                    )}
                  </div>
                </div>
                <div className="w-full lg:w-6/12 px-4">
                  <div className="relative w-full mb-3">
                    <label className="block text-gray-300 text-xs font-bold mb-2">
                      Inventory Images
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      name="inventory_images"
                      onChange={handleChange}
                      disabled={type === "view"}
                      className="border-0 px-3 py-2 placeholder-gray-500 text-white bg-dark-primary rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                      placeholder="Enter event name"
                    />
                    {errors.inventory_images && (
                      <p className="text-red-500 text-xs">
                        {errors.inventory_images}
                      </p>
                    )}
                  </div>
                </div>
                {type !== "create" ? (
                  <>
                    <div className="w-full lg:w-12/12 px-4">
                      <div className="relative w-full mb-3">
                        <label className="block text-gray-300 text-xs font-bold mb-2">
                          Existing Image
                        </label>
                        {formData.existing_images &&
                          formData.existing_images.map((imageUrl, index) => {
                            const newImageUrl = imageUrl;
                            //.replace(/^[^_]*_/, "");
                            return (
                              <>
                                <button
                                  key={index}
                                  type="button"
                                  onClick={() =>
                                    window.open(
                                      `${imageBaseURL}${imageUrl}`,
                                      "_blank"
                                    )
                                  } // opens in new tab
                                  className="w-10/12 bg-dark-primary text-white border border-white/10 rounded-md py-2 px-4 my-2 shadow hover:bg-white/5 transition"
                                >
                                  {newImageUrl}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleImageChange(index)}
                                  style={{ width: "50px" }}
                                  className={` bg-dark-primary text-red-400 hover:text-red-300 border border-white/10 rounded-md py-2 px-4 my-2 shadow hover:bg-white/5 transition ${type === "view" ? "hidden" : ""
                                    }`}
                                >
                                  ✕
                                </button>
                              </>
                            );
                          })}
                      </div>
                    </div>
                  </>
                ) : null}
              </div>
              <hr className="mt-6 border-b-1 border-blueGray-300" />
              <h6 className="text-gray-400 text-sm mt-3 mb-6 font-bold">
                Description
              </h6>
              <div className="flex flex-wrap">
                <div className="w-full lg:w-12/12 px-4">
                  <div className="relative w-full mb-3">
                    <label className="block text-gray-300 text-xs font-bold mb-2">
                      Description
                    </label>
                    <textarea
                      className="border-0 px-3 py-3 placeholder-gray-500 text-white bg-dark-primary rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                      placeholder="Description"
                      rows="4"
                      name="description"
                      readOnly={type === "view"}
                      value={formData.description}
                      onChange={handleChange}
                    />
                    {errors.description && (
                      <p className="text-red-500 text-xs">
                        {errors.description}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex justify-end w-full">
                  <Tooltip title="Back" arrow>
                    <button
                      className="mt-10 text-white bg-blueGray-700 active:bg-blueGray-600 font-semibold text-sm px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-auto ease-linear transition-all duration-150"
                      type="button"
                      onClick={() => navigate("/admin/inventory")}
                    >
                      {"<< "}Back
                    </button>
                  </Tooltip>
                  <Tooltip title="Submit" arrow>
                    <button
                      className={`bg-lightBlue-500 mt-10 text-white active:bg-lightBlue-600 font-semibold text-sm px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-1 ease-linear transition-all duration-150 ${type === "view" ? "hidden" : ""
                        }`}
                      type="submit"
                      disabled={loading || type === "view"}
                    >
                      {loading ? "Creating..." : "Submit"}
                    </button>
                  </Tooltip>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
